<script>
import {
  computed, defineComponent, onBeforeMount,
  ref, watch,
} from "vue";
import {useMeta} from "vue-meta";
import Editor from "primevue/editor";
import {useI18n} from "vue-i18n";
import Skeleton from "primevue/skeleton"
import Button from "primevue/button";
import {useStore} from "vuex";
import { useApiCall } from "@/composables/useApiCall";
import {
  privacyPolicyApiCall,
  termsOfUseApiCall,
  personalDataRulesApiCall,
  updatePrivacyPolicyApiCall,
  updateTermsOfUseApiCall,
  updatePersonalDataRulesApiCall,
} from "@/services/policy"
import MainCard from "@/components/cards/MainCard.vue";
import ButtonSuccess from "@/components/buttons/ButtonSuccess.vue";
export default defineComponent({
  layout: { name: 'AdminLayout' },
  components: {
    ButtonSuccess,
    MainCard,
    Editor,
    Button,
    Skeleton,
  },
  setup() {
    const {t} = useI18n()
    useMeta({
      title: t('menu.blocks'),
    });
    const privacyPolicyFields = ref({text: '', text_en: '', text_ch: ''})
    const termsOfUseFields = ref({text: '', text_en: '', text_ch: ''})
    const personalDataRulesFields = ref({text: '', text_en: '', text_ch: ''})
    const isUpdatedPrivacyPolicy = ref(false)
    const isUpdatedTermsOfUse = ref(false)
    const isUpdatedPersonalDataRules = ref(false)

    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const textField = computed(() => `text${lang.value === 'ru' ? '' : `_${lang.value}`}`);

    const {
      isLoading: isPrivacyPolicyLoading,
      data: privacyPolicyData,
      error: privacyPolicyError,
      executeApiCall: getPrivacyPolicyAction,
    } = useApiCall(privacyPolicyApiCall, true)
    const {
      isLoading: isTermsOfUseLoading,
      data: termsOfUseData,
      error: termsOfUseError,
      executeApiCall: getTermsOfUseAction,
    } = useApiCall(termsOfUseApiCall, true)
    const {
      isLoading: isPersonalDataRulesLoading,
      data: personalDataRulesData,
      error: personalDataRulesError,
      executeApiCall: personalDataRulesAction,
    } = useApiCall(personalDataRulesApiCall, true)

    const {
      isLoading: isUpdatePrivacyPolicyLoading,
      data: updatePrivacyPolicyData,
      error: updatePrivacyPolicyError,
      executeApiCall: updatePrivacyPolicyAction,
    } = useApiCall(updatePrivacyPolicyApiCall, true)
    const {
      isLoading: isUpdateTermsOfUseLoading,
      data: updateTermsOfUseData,
      error: updateTermsOfUseError,
      executeApiCall: updateTermsOfUseAction,
    } = useApiCall(updateTermsOfUseApiCall, true)
    const {
      isLoading: isUpdatePersonalDataRulesLoading,
      data: updatePersonalDataRulesData,
      error: updatePersonalDataRulesError,
      executeApiCall: updatePersonalDataRulesAction,
    } = useApiCall(updatePersonalDataRulesApiCall, true)

    const updatePrivacyPolicy = async () => {
      try {
        const {text, text_en, text_ch} = privacyPolicyFields.value
        await updatePrivacyPolicyAction({text, text_en, text_ch})
        isUpdatedPrivacyPolicy.value = true
      } catch (e) {
        console.error(e)
      }
    }

    const updateTermsOfUse = async () => {
      try {
        const {text, text_en, text_ch} = termsOfUseFields.value
        await updateTermsOfUseAction({text, text_en, text_ch})
        isUpdatedTermsOfUse.value = true
      } catch (e) {
        console.error(e)
      }
    }

    const updatePersonalDataRules = async () => {
      try {
        const {text, text_en, text_ch} = personalDataRulesFields.value
        await updatePersonalDataRulesAction({text, text_en, text_ch})
        isUpdatedPersonalDataRules.value = true
      } catch (e) {
        console.error(e)
      }
    }

    watch(() => privacyPolicyFields.value,
        () => {
          isUpdatedPrivacyPolicy.value = false
        }, {deep: true})

    watch(() => termsOfUseFields.value,
        () => {
          isUpdatedTermsOfUse.value = false
        }, {deep: true})

    watch(() => personalDataRulesFields.value,
        () => {
          isUpdatedPersonalDataRules.value = false
        }, {deep: true})

    onBeforeMount(async () => {
      try {
        await getPrivacyPolicyAction()
        if (privacyPolicyData.value) {
          const {text, text_en, text_ch} = privacyPolicyData.value.data
          privacyPolicyFields.value = {text, text_en, text_ch}
        }
      } catch (e) {
        console.error(e)
      }
      try {
        await getTermsOfUseAction()
        if (termsOfUseData.value) {
          const {text, text_en, text_ch} = termsOfUseData.value.data
          termsOfUseFields.value = {text, text_en, text_ch}
        }
      } catch (e) {
        console.error(e)
      }
      try {
        await personalDataRulesAction()
        if (personalDataRulesData.value) {
          const {text, text_en, text_ch} = personalDataRulesData.value.data
          personalDataRulesFields.value = {text, text_en, text_ch}
        }
      } catch (e) {
        console.error(e)
      }
    })

    return {
      t,
      privacyPolicyFields,
      termsOfUseFields,
      personalDataRulesFields,
      isUpdatedPrivacyPolicy,
      isUpdatedTermsOfUse,
      isUpdatedPersonalDataRules,
      updatePrivacyPolicy,
      updateTermsOfUse,
      updatePersonalDataRules,
      isPrivacyPolicyLoading,
      isTermsOfUseLoading,
      isPersonalDataRulesLoading,
      isUpdatePrivacyPolicyLoading,
      isUpdateTermsOfUseLoading,
      isUpdatePersonalDataRulesLoading,
      updatePrivacyPolicyError,
      updateTermsOfUseError,
      updatePersonalDataRulesError,
      textField,
    };
  },
})
</script>

<template>
  <div class="relative">
    <section v-if="!isPrivacyPolicyLoading">
      <div class="flex justify-content-between align-items-center mb-4">
        <h2>{{ t('policy.privacy') }}</h2>
        <ButtonSuccess v-if="isUpdatedPrivacyPolicy" :label="t('button.changesSaved')" class="btn-fix-height"/>
        <Button v-else @click="updatePrivacyPolicy" :loading="isUpdatePrivacyPolicyLoading" :label="t('button.saveChange')" class="btn-primary font-light btn-fix-height" />
      </div>
      <MainCard :title="t('websiteCard.infrastructure.input')">
        <Editor v-model="privacyPolicyFields[textField]"></Editor>
        <span v-if="updatePrivacyPolicyError?.data[textField]" class="flex text-xs color-error">
        {{ updatePrivacyPolicyError?.data[textField][0] }}
      </span>
      </MainCard>
    </section>
    <Skeleton v-else height="256px"></Skeleton>

    <section v-if="!isTermsOfUseLoading" class="mt-6">
      <div class="flex justify-content-between align-items-center mb-4">
        <h2>{{ t('policy.user') }}</h2>
        <ButtonSuccess v-if="isUpdatedTermsOfUse" :label="t('button.changesSaved')" class="btn-fix-height"/>
        <Button v-else @click="updateTermsOfUse" :loading="isUpdateTermsOfUseLoading" :label="t('button.saveChange')" class="btn-primary font-light btn-fix-height" />
      </div>
      <MainCard :title="t('websiteCard.infrastructure.input')">
        <Editor v-model="termsOfUseFields[textField]"></Editor>
        <span v-if="updateTermsOfUseError?.data[textField]" class="flex text-xs color-error">
        {{ updateTermsOfUseError?.data[textField][0] }}
      </span>
      </MainCard>
    </section>
    <Skeleton v-else height="256px"></Skeleton>

    <section v-if="!isPersonalDataRulesLoading" class="mt-6">
      <div class="flex justify-content-between align-items-center mb-4">
        <h2>{{ t('policy.rules') }}</h2>
        <ButtonSuccess v-if="isUpdatedPersonalDataRules" :label="t('button.changesSaved')" class="btn-fix-height"/>
        <Button v-else @click="updatePersonalDataRules" :loading="isUpdatePersonalDataRulesLoading" :label="t('button.saveChange')" class="btn-primary font-light btn-fix-height" />
      </div>
      <MainCard :title="t('websiteCard.infrastructure.input')">
        <Editor v-model="personalDataRulesFields[textField]"></Editor>
        <span v-if="updatePersonalDataRulesError?.data[textField]" class="flex text-xs color-error">
        {{ updatePersonalDataRulesError?.data[textField][0] }}
      </span>
      </MainCard>
    </section>
    <Skeleton v-else height="256px"></Skeleton>
  </div>
</template>
<style lang="scss" scoped>
</style>